import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './style.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FRAME_BASE = '/assets/file/images/';

// Product frame mockups that have a .webp + the photo-window inset (% of the
// square frame box) mirroring the editor's cropper-frame padding, so each
// square preview lands inside the real frame aperture.
const FRAME_IMG = {
  classic: 'frame_classic.webp',
  village: 'frame_village.webp',
  clean: 'frame_clean.webp',
  bold: 'frame_bold.webp',
  nordic: 'frame_nordic.webp',
};
const FRAME_INSETS = {
  classic: { top: 15.8, right: 17.1, bottom: 17.9, left: 14.5 },
  village: { top: 15.8, right: 17.1, bottom: 17.9, left: 14.5 },
  clean: { top: 13.7, right: 15.8, bottom: 16.3, left: 13.9 },
  bold: { top: 3.7, right: 6.3, bottom: 6.6, left: 3.9 },
  nordic: { top: 3.7, right: 6.3, bottom: 6.6, left: 3.9 },
};
const DEFAULT_INSET = { top: 6, right: 6, bottom: 6, left: 6 };

// Both results are the same perfect square — two AI fill styles to choose from.
const OPTIONS = [
  { key: 1, label: 'מילוי טבעי', hint: 'המשך ריאליסטי של הסצנה' },
  { key: 2, label: 'רקע רך', hint: 'רקע נקי ועדין' },
];

const Sparkle = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.4c.55 5.6 2.1 7.15 7.6 7.6-5.5.45-7.05 2-7.6 7.6-.55-5.6-2.1-7.15-7.6-7.6 5.5-.45 7.05-2 7.6-7.6Z" />
    <path d="M19 14.6c.2 2.1.85 2.75 3 3-2.15.25-2.8.9-3 3-.2-2.1-.85-2.75-3-3 2.15-.25 2.8-.9 3-3Z" />
  </svg>
);

const ExpandChoiceModal = ({ imageUrl, imagePublicId, frame, onClose, onSelect }) => {
  const [isGenerating, setIsGenerating] = useState(true);
  const [previews, setPreviews] = useState(null);
  const [publicId, setPublicId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState({ 1: false, 2: false });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const frameImg = FRAME_IMG[frame] ? FRAME_BASE + FRAME_IMG[frame] : null;
  const inset = FRAME_INSETS[frame] || DEFAULT_INSET;

  const generate = useCallback(async () => {
    setIsGenerating(true);
    setError(null);
    setPreviews(null);
    setSelected(null);
    setLoaded({ 1: false, 2: false });

    try {
      const res = await axios.post(`${BASE_URL}/image-expand`, { imageUrl }, { timeout: 120000 });
      if (res.data.success) {
        setPreviews({ url1: res.data.previewUrl1, url2: res.data.previewUrl2, squareSize: res.data.squareSize });
        setPublicId(res.data.publicId);
      } else {
        setError(res.data.message || 'לא הצלחנו ליצור הרחבה');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'לא הצלחנו ליצור הרחבה');
    } finally {
      setIsGenerating(false);
    }
  }, [imageUrl]);

  // One tap from the editor — start the magic the moment we open.
  useEffect(() => { generate(); }, [generate]);

  const handleSave = async () => {
    if (!selected || !publicId || !previews) return;
    const chosenPreviewUrl = selected === 1 ? previews.url1 : previews.url2;
    setIsSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${BASE_URL}/image-expand/save`, {
        chosenPreviewUrl, publicId, imagePublicId,
      }, { timeout: 120000 });
      if (res.data.success) {
        onSelect(res.data.url, previews.squareSize, previews.squareSize);
      } else {
        setError(res.data.message || 'לא הצלחנו לשמור את התמונה');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'לא הצלחנו לשמור את התמונה');
    } finally {
      setIsSaving(false);
    }
  };

  const cleanup = () => {
    if (publicId) axios.post(`${BASE_URL}/image-expand/cleanup`, { publicId }).catch(() => {});
  };
  const handleClose = () => { cleanup(); onClose(); };
  const handleRetry = () => { cleanup(); setPublicId(null); generate(); };

  const renderCard = (opt, url) => {
    const isLoaded = loaded[opt.key];
    const isSel = selected === opt.key;
    const ready = !!url;
    return (
      <button
        key={opt.key}
        type="button"
        className={`expc-opt${isSel ? ' expc-opt--selected' : ''}${frameImg ? '' : ' expc-opt--plain'}`}
        onClick={() => ready && isLoaded && setSelected(opt.key)}
        aria-pressed={isSel}
        disabled={!ready}
      >
        <div className="expc-opt-frame" style={frameImg ? { backgroundImage: `url(${frameImg})` } : undefined}>
          <div
            className="expc-opt-window"
            style={{ top: `${inset.top}%`, right: `${inset.right}%`, bottom: `${inset.bottom}%`, left: `${inset.left}%` }}
          >
            {(!ready || !isLoaded) && <span className="expc-shimmer" />}
            {ready && (
              <img
                src={url}
                alt={opt.label}
                style={{ opacity: isLoaded ? 1 : 0 }}
                onLoad={() => setLoaded((p) => ({ ...p, [opt.key]: true }))}
                onError={() => setError('שגיאה בטעינת ההרחבה, נסו שוב')}
              />
            )}
          </div>
          <span className="expc-opt-check" aria-hidden="true">
            <img src="/assets/images/checkout_checkw.svg" alt="" />
          </span>
        </div>
        <span className="expc-opt-label">
          {opt.label}
          <span className="expc-opt-hint">{opt.hint}</span>
        </span>
      </button>
    );
  };

  const showError = error && !previews;

  return (
    <div className="expc-overlay" onClick={handleClose}>
      <div className="expc-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="expc-close" onClick={handleClose} aria-label="סגירה">&times;</button>

        <div className="expc-header">
          <span className="expc-eyebrow"><Sparkle className="expc-eyebrow-spark" /> הרחבה חכמה</span>
          <h3 className="expc-title">ממלאים את המסגרת</h3>
          <p className="expc-subtitle">
            {previews
              ? 'שתי גרסאות ריבועיות שמתאימות בול למסגרת — בחרו את האהובה עליכם'
              : 'ה‑AI משלים את התמונה לריבוע מושלם…'}
          </p>
        </div>

        {showError ? (
          <div className="expc-original">
            <img className="expc-original-img" src={imageUrl} alt="התמונה המקורית" />
          </div>
        ) : (
          <div className="expc-options">
            {OPTIONS.map((opt) => renderCard(opt, previews ? (opt.key === 1 ? previews.url1 : previews.url2) : null))}
          </div>
        )}

        {error && <div className="expc-error">{error}</div>}

        <div className="expc-actions">
          {previews && (
            <button type="button" className="expc-btn expc-btn--primary" onClick={handleSave} disabled={!selected || isSaving}>
              {isSaving ? 'שומרים…' : 'בחירה'}
            </button>
          )}
          {(previews || showError) && (
            <button type="button" className="expc-btn expc-btn--ghost" onClick={handleRetry} disabled={isSaving}>
              {showError ? 'נסו שוב' : 'גרסאות חדשות'}
            </button>
          )}
          <button type="button" className="expc-btn expc-btn--text" onClick={handleClose} disabled={isSaving}>
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpandChoiceModal;
